import React from 'react';
import { Radio, Space, Form, FormInstance } from 'antd';
import { useQuery } from '@common/hooks';
import { ErrorHandler, LoadingState, TextArea } from '@common/components';
import { AvailableEnumsKeys, ENUM_QUERY } from '@common/graphql';

export type RejectionDrawerValues = {
  rejectionReason: string;
  rejectionComment: string;
};

type Props = {
  form: FormInstance;
  onRejectClick: (values: RejectionDrawerValues) => void;
  reasonsListQuery: AvailableEnumsKeys;
};

export function RejectionForm({
  form,
  onRejectClick,
  reasonsListQuery,
}: Props) {
  const { loading, error, data } = useQuery(ENUM_QUERY, {
    variables: {
      key: reasonsListQuery,
    },
  });

  if (loading && !data) {
    return <LoadingState />;
  }

  if (error) {
    return (
      <ErrorHandler
        error={error}
        log={`ComplianceItemRejectionForm: ${reasonsListQuery}`}
      />
    );
  }

  const reasons = data?.enum;

  return (
    <Form
      layout="vertical"
      form={form}
      initialValues={{ rejectionReason: reasons?.[0]?.name }}
      onFinish={onRejectClick}
    >
      <Form.Item
        colon={false}
        label="Rejection Category"
        name="rejectionReason"
        rules={[
          {
            required: true,
            message: 'Please select a rejection category',
          },
        ]}
      >
        <Radio.Group>
          <Space direction="vertical">
            {reasons?.map((reason) => (
              <Radio key={reason.name} value={reason.name}>
                {reason.description}
              </Radio>
            ))}
          </Space>
        </Radio.Group>
      </Form.Item>
      <Form.Item
        colon={false}
        label="Comment"
        name="rejectionComment"
        rules={[
          { required: true, message: 'Please provide a comment' },
          {
            type: 'string',
            min: 1,
            max: 500,
            message: 'Comment cannot be more than 500 characters.',
          },
        ]}
      >
        <TextArea
          rows={2}
          placeholder="Please enter a comment for the rejection (required)"
          maxLength={500}
        />
      </Form.Item>
    </Form>
  );
}
